/**
 * Componente de Dialog de Redirecionamento do Pacote Cenoura
 * Exibe um overlay com progress circular e texto antes de executar uma ação
 * Verifica conectividade a cada segundo e redireciona assim que a conexão for restaurada
 */

import { carrotService } from '../services/carrot.service';
import { logger } from '../utils/logger.util';

export class CarrotRedirectDialogComponent {
  private overlay: HTMLElement | null = null;
  private countdown: number = 0;
  private remaining: number = 0;
  private onComplete: (() => void | Promise<void>) | null = null;
  private timer: number | null = null;
  private isCheckingConnectivity: boolean = false;

  /**
   * Método estático para abrir o dialog de redirecionamento
   * @param countdown - Tempo em segundos para o countdown
   * @param onComplete - Callback executado quando o countdown termina
   */
  static show(countdown: number, onComplete: () => void | Promise<void>): CarrotRedirectDialogComponent {
    const dialog = new CarrotRedirectDialogComponent();
    dialog.open(countdown, onComplete);
    return dialog;
  }

  /**
   * Abre o dialog
   */
  private open(countdown: number, onComplete: () => void | Promise<void>): void {
    if (countdown <= 0) {
      // Se não há countdown, executar ação diretamente
      onComplete();
      return;
    }

    this.countdown = countdown;
    this.remaining = countdown;
    this.onComplete = onComplete;

    // Criar overlay e dialog
    this.createDialog();
    this.attachEvents();

    // Iniciar countdown
    this.startCountdown();
  }

  /**
   * Cria o dialog no DOM
   */
  private createDialog(): void {
    logger.debug('[CarrotRedirectDialogComponent] Criando dialog...');
    
    // Criar overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'carrot-redirect-overlay';
    
    // Aplicar estilos inline como fallback
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;
    
    this.overlay.innerHTML = `
      <div class="carrot-redirect-dialog" style="
        background-color: white;
        border-radius: 16px;
        padding: 40px 32px;
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 280px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      ">
        <div class="carrot-redirect-progress-container" style="
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div class="carrot-redirect-progress-circle" style="
            width: 80px;
            height: 80px;
            position: relative;
          ">
            <svg class="carrot-redirect-progress-svg" viewBox="0 0 36 36" style="
              width: 100%;
              height: 100%;
              transform: rotate(-90deg);
            ">
              <circle class="carrot-redirect-progress-bg" cx="18" cy="18" r="16" style="
                fill: none;
                stroke: rgba(102, 0, 153, 0.1);
                stroke-width: 3;
              "></circle>
              <circle class="carrot-redirect-progress-fill" cx="18" cy="18" r="16" id="carrot-progress-circle" style="
                fill: none;
                stroke: #660099;
                stroke-width: 3;
                stroke-linecap: round;
              "></circle>
            </svg>
            <span id="carrot-countdown-text" style="
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              font-size: 24px;
              font-weight: 600;
              color: #660099;
              font-family: var(--font-family-vivo, 'Vivo Type', sans-serif);
            ">${this.remaining}</span>
          </div>
        </div>
        <p class="carrot-redirect-message" style="
          font-size: 16px;
          color: rgba(0, 0, 0, 0.87);
          font-weight: 400;
          text-align: center;
          margin: 0;
          line-height: 1.5;
        ">
          Aguarde, redirecionando
        </p>
      </div>
    `;

    // Adicionar ao body
    document.body.appendChild(this.overlay);
    logger.debug('[CarrotRedirectDialogComponent] Dialog criado e adicionado ao DOM');
  }

  /**
   * Anexa eventos
   */
  private attachEvents(): void {
    // Prevenir fechamento com ESC
    document.addEventListener('keydown', this.preventClose);
  }

  private preventClose = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  /**
   * Inicia o countdown e verifica conectividade a cada segundo
   */
  private startCountdown(): void {
    const progressCircle = this.overlay?.querySelector('#carrot-progress-circle') as SVGCircleElement;
    const countdownText = this.overlay?.querySelector('#carrot-countdown-text') as HTMLElement;
    
    if (!progressCircle) {
      logger.error('[CarrotRedirectDialogComponent] Elemento de progress não encontrado');
      return;
    }

    if (!countdownText) {
      logger.error('[CarrotRedirectDialogComponent] Elemento de contador não encontrado');
      return;
    }

    const circumference = 2 * Math.PI * 16; // raio = 16
    const totalSeconds = this.countdown;

    // Configurar SVG
    progressCircle.style.strokeDasharray = `${circumference} ${circumference}`;
    progressCircle.style.strokeDashoffset = circumference.toString();

    const updateCountdown = async () => {
      // Verificar conectividade a cada segundo (sem bloquear a atualização visual)
      this.checkConnectivityAsync();

      this.remaining--;

      // Atualizar texto do contador
      if (countdownText) {
        countdownText.textContent = this.remaining.toString();
      }

      // Atualizar progresso do círculo
      const progress = (totalSeconds - this.remaining) / totalSeconds;
      const offset = circumference * (1 - progress);
      progressCircle.style.strokeDashoffset = offset.toString();

      if (this.remaining <= 0) {
        // Countdown terminou
        this.stopCountdown();
        this.executeCallback();
      } else {
        // Continuar countdown
        this.timer = window.setTimeout(updateCountdown, 1000);
      }
    };

    // Iniciar countdown
    this.timer = window.setTimeout(updateCountdown, 1000);
  }

  /**
   * Verifica conectividade de forma assíncrona (não bloqueia o countdown)
   * Se a conectividade for restaurada, executa o callback imediatamente
   */
  private async checkConnectivityAsync(): Promise<void> {
    // Evitar múltiplas verificações simultâneas
    if (this.isCheckingConnectivity) {
      return;
    }

    this.isCheckingConnectivity = true;

    try {
      const isConnected = await carrotService.checkInternetConnection();
      
      if (isConnected) {
        logger.debug('[CarrotRedirectDialogComponent] Conectividade restaurada! Redirecionando imediatamente...');
        // Conectividade restaurada - executar callback e fechar dialog
        this.stopCountdown();
        await this.executeCallback();
      }
    } catch (error) {
      logger.error('[CarrotRedirectDialogComponent] Erro ao verificar conectividade:', error);
      // Em caso de erro, continuar o countdown normalmente
    } finally {
      this.isCheckingConnectivity = false;
    }
  }

  /**
   * Para o countdown
   */
  private stopCountdown(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.isCheckingConnectivity = false;
    document.removeEventListener('keydown', this.preventClose);
  }

  /**
   * Executa o callback e fecha o dialog
   */
  private async executeCallback(): Promise<void> {
    if (this.onComplete) {
      try {
        await this.onComplete();
      } catch (error) {
        logger.error('[CarrotRedirectDialogComponent] Erro ao executar callback:', error);
      }
    }
    // Fechar dialog
    this.close();
  }

  /**
   * Fecha o dialog
   */
  close(): void {
    this.stopCountdown();
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }
}
